import { useEffect, useState, type JSX } from "react";

import { ExclamationCircleFilled } from "@ant-design/icons";
import {
    formatDateTime,
    formatFileSize,
} from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Button, CollapseTransition, Svg } from "@impero-com/ui";
import * as Sentry from "@sentry/react";
import { Popconfirm, Progress, Typography, ProgressProps } from "antd";
import { cx } from "class-variance-authority";

import deleteSvg from "img/delete.svg";

import * as theme from "./theme.css";
import { Status, UploaderFileBase } from "./types";
import { WARN_EXTENSIONS } from "./utils";

const { Text, Link } = Typography;

const uploadDisplayStatusMap: Record<Status, ProgressProps["status"]> = {
    done: "success",
    success: "success",
    error: "exception",
    missing: "exception",
    removed: "normal",
    uploading: "normal",
};
const UPLOADING_PROGRESS_CAP = 95;
const HIDE_PROGRESS_DELAY = 1000;
const MAX_PERCENT = 100;
const MIN_PERCENT = 0;
interface FileUploadIndicatorProps {
    percent: number | undefined;
    status: Status;
}
const FileUploadIndicator = ({
    percent,
    status,
}: FileUploadIndicatorProps): JSX.Element => {
    const [showProgress, setShowProgress] = useState(!!percent);

    // Depending on status, updates showProgress with a delay
    useEffect(() => {
        let timeoutId: NodeJS.Timeout | undefined;
        switch (status) {
            case "success":
            case "done":
            case "error":
            case "removed":
            case "missing":
                if (!showProgress) {
                    break;
                }
                timeoutId = setTimeout(() => {
                    setShowProgress(false);
                }, HIDE_PROGRESS_DELAY);
                break;
            case "uploading":
                // eslint-disable-next-line react-hooks/set-state-in-effect
                setShowProgress(true);
                break;
            default:
                assertNever(status);
        }
        return () => clearTimeout(timeoutId);
    }, [percent, showProgress, status]);

    const displayPercentMap: Record<Status, number> = {
        uploading: percent
            ? Math.min(UPLOADING_PROGRESS_CAP, percent)
            : MAX_PERCENT,
        error: percent ?? MAX_PERCENT,
        missing: MAX_PERCENT,
        done: MAX_PERCENT,
        success: MAX_PERCENT,
        removed: MIN_PERCENT,
    };
    const displayedPercent = displayPercentMap[status];
    return (
        <CollapseTransition collapsed={!showProgress}>
            <Progress
                status={uploadDisplayStatusMap[status]}
                showInfo={false}
                percent={displayedPercent}
            />
        </CollapseTransition>
    );
};

interface FileLinkWithWarnProps {
    url?: string;
    disabled: boolean;
    fileName: string;
}
const FileLinkWithWarn = ({
    url,
    disabled,
    fileName,
}: FileLinkWithWarnProps) => {
    const handleDownload = () => {
        if (disabled) {
            const err = new Error(
                "Click handler was fired when component should be disabled!"
            );
            console.error(err);
            Sentry.captureException(err);
            return;
        }
        window.open(url, "_self");
    };
    const linkClassNames = cx(!disabled && theme.linkText);

    const lastDotIndex = fileName.lastIndexOf(".");
    const extension = fileName.substring(lastDotIndex).toLowerCase();
    const isFileSafe = !WARN_EXTENSIONS.has(extension);

    return (
        <Popconfirm
            title={t("AddFiles_Warning")}
            disabled={isFileSafe}
            onConfirm={handleDownload}
            okText={t("Confirm")}
            cancelText={t("Cancel")}
        >
            <div className={theme.linkContainer}>
                <Link
                    disabled={disabled}
                    className={theme.link}
                    href={url}
                    onClick={(e) =>
                        isFileSafe ? handleDownload() : e.preventDefault()
                    }
                >
                    <Text
                        className={linkClassNames}
                        ellipsis={{ tooltip: fileName }}
                    >
                        {fileName}
                    </Text>
                </Link>
            </div>
        </Popconfirm>
    );
};

interface FileProps extends UploaderFileBase {
    deleteDisabled: boolean;
    status: "success" | "done" | "error" | "uploading" | "missing";
    percent?: number;
    error?: string;
    url?: string;
    onDeleteClick: () => void;
}
export const File = ({
    fileName,
    lastModified,
    fileSize,
    status,
    deleteDisabled,
    onDeleteClick,
    url,
    percent,
    error,
}: FileProps): JSX.Element => {
    const isUploadedStatus = status === "success" || status === "done";
    const isSuccessStatus = status === "success";
    const isUploadingStatus = status === "uploading";
    const isErrorStatus = status === "error" || status === "missing";

    const sizeFormatted = formatFileSize(fileSize);
    const dateFormatted = lastModified
        ? formatDateTime(new Date(lastModified))
        : undefined;
    const rootClassNames = cx(
        theme.file,
        isErrorStatus && theme.fileErrored,
        isSuccessStatus && theme.fileSuccess
    );
    const deleteIconClasses = cx(
        theme.deleteIcon,
        isUploadingStatus && theme.deleteIconDisabled
    );

    const handleDeleteClick = () => {
        switch (status) {
            case "done":
            case "success":
            case "error":
            case "missing":
                if (!deleteDisabled) {
                    onDeleteClick();
                }
                break;
            case "uploading":
                const err = new Error(
                    "Click handler was fired when component should be disabled!"
                );
                console.error(err);
                Sentry.captureException(err);
                break;
            default:
                assertNever(status);
        }
    };

    return (
        <div className={rootClassNames}>
            <div className={theme.fileContent}>
                <div className={theme.fileInfoContainer}>
                    <div className={theme.fileInfo}>
                        <FileLinkWithWarn
                            fileName={fileName}
                            url={isUploadedStatus ? url : undefined}
                            disabled={!isUploadedStatus}
                        />
                        <div className={theme.dateSizeContainer}>
                            {dateFormatted && (
                                <Text type="secondary">{dateFormatted}</Text>
                            )}
                            <Text type="secondary">{sizeFormatted}</Text>
                        </div>
                    </div>

                    {/* Not perfect - it cancels the progress transition to red - but they can't both render at the same time */}
                    {isErrorStatus ? (
                        <div className={theme.errorContainer}>
                            <ExclamationCircleFilled
                                className={theme.errorText}
                            />
                            <Text className={theme.errorText}>{error}</Text>
                        </div>
                    ) : (
                        <FileUploadIndicator
                            status={status}
                            percent={isUploadingStatus ? percent : undefined}
                        />
                    )}
                </div>

                {!deleteDisabled && (
                    <Button
                        disabled={isUploadingStatus}
                        className={theme.deleteButton}
                        onClick={handleDeleteClick}
                        danger
                        variant="link"
                    >
                        <Svg className={deleteIconClasses} src={deleteSvg} />
                    </Button>
                )}
            </div>
        </div>
    );
};
